import React, { createContext, useContext, useState, useEffect, useRef, useCallback, useMemo, ReactNode } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';

// BUG_LIST #146: the search box's per-keystroke `inputValue` used to live in the
// same context as the submitted `searchQuery`, so every keystroke re-rendered
// every consumer — including Dashboard's unmemoized SchoolCard grid. We split
// the two: `inputValue` lives in its own context consumed ONLY by the search box
// (AppLayout), while the submit outputs (`searchQuery`/`searchNonce`) live in a
// separate, keystroke-stable context that Dashboard reads. handleSearch reads
// the latest input through a ref so its identity never changes per keystroke.

interface SearchInputContextType {
  inputValue: string;
  setInputValue: (value: string) => void;
}

interface SearchQueryContextType {
  searchQuery: string;
  // Bumped on every explicit search submit, even when the term is unchanged, so
  // consumers can re-run a search for a term already in the box.
  searchNonce: number;
  isOnDashboard: boolean;
  handleSearch: () => void;
  clearSearch: () => void;
}

const SearchInputContext = createContext<SearchInputContextType | undefined>(undefined);
const SearchQueryContext = createContext<SearchQueryContextType | undefined>(undefined);

export const SearchProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [inputValue, setInputValue] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [searchNonce, setSearchNonce] = useState(0);

  const isOnDashboard = location.pathname === '/dashboard';

  // Latest input value, without making handlers depend on it (keystroke-stable).
  const inputRef = useRef(inputValue);
  inputRef.current = inputValue;
  const isOnDashboardRef = useRef(isOnDashboard);
  isOnDashboardRef.current = isOnDashboard;

  // Clear search query when input is emptied.
  useEffect(() => {
    if (!inputValue.trim() && searchQuery) {
      setSearchQuery('');
    }
  }, [inputValue]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleSearch = useCallback(() => {
    const trimmedValue = inputRef.current.trim();

    if (!trimmedValue) {
      setSearchQuery('');
      return;
    }

    setSearchQuery(trimmedValue);
    setSearchNonce((n) => n + 1);

    if (!isOnDashboardRef.current) {
      navigate('/dashboard?view=all&search=true');
    }
  }, [navigate]);

  const clearSearch = useCallback(() => {
    setInputValue('');
    setSearchQuery('');
  }, []);

  const inputContextValue = useMemo<SearchInputContextType>(
    () => ({ inputValue, setInputValue }),
    [inputValue],
  );

  // Keystroke-stable: only changes on submit / navigation, never per keystroke.
  const queryContextValue = useMemo<SearchQueryContextType>(
    () => ({ searchQuery, searchNonce, isOnDashboard, handleSearch, clearSearch }),
    [searchQuery, searchNonce, isOnDashboard, handleSearch, clearSearch],
  );

  return (
    <SearchQueryContext.Provider value={queryContextValue}>
      <SearchInputContext.Provider value={inputContextValue}>
        {children}
      </SearchInputContext.Provider>
    </SearchQueryContext.Provider>
  );
};

// For the search box only (AppLayout). Re-renders per keystroke — that's fine,
// it's the input itself.
export const useSearchInput = () => {
  const context = useContext(SearchInputContext);
  if (context === undefined) {
    throw new Error('useSearchInput must be used within a SearchProvider');
  }
  return context;
};

// For everyone reading the submitted search (Dashboard). Does NOT re-render on
// keystrokes.
export const useSearch = () => {
  const context = useContext(SearchQueryContext);
  if (context === undefined) {
    throw new Error('useSearch must be used within a SearchProvider');
  }
  return context;
};
